import { CaseDetail } from '../models/DataAdminTypes';

/**
 * Extracts a list of unique case IDs from an array of CaseDetail objects.
 * This effectively removes duplicates caused by multiple stages sharing the same ID.
 *
 * @param cases - An array of CaseDetail objects to process.
 * @returns An array of unique strings representing the case IDs.
 *
 * @example
 * const ids = getUniqueCaseIds(caseList);
 * // returns ["2334", "kdoyle12345"]
 */
export const getUniqueCaseIds = (cases: CaseDetail[]): string[] => {
  return Array.from(new Set(cases.map(c => c.caseId)));
};

/**
 * Sorts a list of strings using natural ascending order.
 *
 * Rules:
 * - Pure numeric strings (e.g., "001", "33") are compared by their integer value.
 * - Alphanumeric strings with trailing digits (e.g., "a2", "a100") are sorted
 *   first by the non-digit prefix (case-insensitive), then by the trailing number.
 * - All other strings are sorted lexicographically (case-insensitive).
 * - When mixing categories, empty prefix (pure numbers) comes before any non-empty prefix.
 *
 * Examples:
 * - ["100", "33", "2"] -> ["2", "33", "100"]
 * - ["a100", "a2", "a33"] -> ["a2", "a33", "a100"]
 *
 * @param items - Array of strings to sort
 * @returns A new array sorted in ascending natural order
 */
export const sortAlphaNumericStrings = (items: string[]): string[] => {
  type SortKey = { prefix: string; num: number | null };

  const toSortKey = (raw: string): SortKey => {
    const s = (raw ?? '').trim();
    // Pure number: compare by integer value
    if (/^\d+$/.test(s)) {
      return { prefix: '', num: parseInt(s, 10) };
    }
    // Alphanumeric with trailing digits: prefix + numeric suffix
    const m = s.match(/^([^\d]+)(\d+)$/);
    if (m) {
      return { prefix: m[1].toLowerCase(), num: parseInt(m[2], 10) };
    }
    // Fallback: lexicographic compare only
    return { prefix: s.toLowerCase(), num: null };
  };

  const cmp = (a: string, b: string): number => {
    const ka = toSortKey(a);
    const kb = toSortKey(b);

    if (ka.prefix !== kb.prefix) {
      return ka.prefix < kb.prefix ? -1 : 1;
    }
    // Same prefix: numeric parts decide if present
    if (ka.num !== null && kb.num !== null) {
      return ka.num - kb.num;
    }
    if (ka.num === null && kb.num !== null) return -1; // plain prefix before numbered variant
    if (ka.num !== null && kb.num === null) return 1;
    // Neither has numeric part: fallback to full-string, case-insensitive
    const la = (a ?? '').toLowerCase();
    const lb = (b ?? '').toLowerCase();
    if (la === lb) return 0;
    return la < lb ? -1 : 1;
  };

  return [...items].sort(cmp);
};
